"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Sun, Globe, Calculator, BookOpen, Telescope } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function HeliocentricPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        <AboutSidebar activeSection="astrology" />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-amber-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-amber-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-amber-500">/</span>
                <span className="text-sm">Heliocentric Astroloji</span>
              </div>
            </div>

            <div className="mb-6">
              <Link href="/about" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/astrology" className="text-slate-400 hover:text-slate-200 transition-colors">
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/astrology/advanced-systems" className="text-slate-400 hover:text-slate-200 transition-colors">
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Heliocentric Astroloji</span>
            </div>

            <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 rounded-xl p-8 mb-8 border border-amber-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Star className="h-10 w-10 text-amber-400" />
                Heliocentric Astroloji
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Güneş merkezli hesaplama sistemi. Kişinin evrensel misyonu, büyük resmi görme yetisi
                ve kosmik bilinç seviyesini inceleyen ileri düzey astroloji yaklaşımıdır.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Geocentric vs Heliocentric</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5 text-blue-400" />
                    Geocentric (Geleneksel)
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">Dünya merkezli bakış açısı - kişisel deneyim odaklı</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Bireysel kişilik analizi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Günlük yaşam deneyimleri</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Ego ve kişisel istek</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Sun className="h-5 w-5 text-amber-400" />
                    Heliocentric (Güneş Merkezli)
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">Güneş merkezli bakış açısı - evrensel misyon odaklı</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Ruhsal misyon ve amaç</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Kosmik bilinç seviyesi</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Evrensel hizmet</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Hesaplama Yöntemi</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-green-400" />
                      Temel Farklılıklar
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-4">
                        <div className="space-y-2 text-sm text-slate-300">
                          <div>• Dünya bir gezegen olarak haritaya eklenir</div>
                          <div>• Güneş merkez noktası olur (ev sistemleri yok)</div>
                          <div>• Ay'ın konumu Dünya'ya göre hesaplanır</div>
                          <div>• Aspectler farklı görünür</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Star className="h-5 w-5 text-orange-400" />
                      Gezegen Sıralaması
                    </h3>
                    <div className="space-y-2">
                      <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-3">
                        <div className="text-sm text-slate-300">
                          <div className="font-medium mb-2">Güneş Sisteminde Sıralama:</div>
                          <div>☿ Merkür → ♀ Venüs → ⊕ Dünya+Ay → ♂ Mars</div>
                          <div>♃ Jüpiter → ♄ Satürn → ♅ Uranüs → ♆ Neptün → ♇ Pluto</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Heliocentric Analiz</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-cyan-300">Dünya'nın Konumu</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 font-medium">Kişinin fiziksel varlığı ve maddi misyonu</p>
                    <div className="space-y-2 text-sm">
                      <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3">
                        <div className="font-medium text-cyan-200 mb-2">Dünya'nın Burcu:</div>
                        <div className="text-slate-300">Fiziksel dünyada nasıl işlev gördüğünüzü gösterir</div>
                      </div>
                      <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3">
                        <div className="font-medium text-cyan-200 mb-2">Dünya Aspectleri:</div>
                        <div className="text-slate-300">Maddi dünyadaki yetenekler ve zorluklar</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-purple-300">Diğer Gezegenlerin Rolü</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 font-medium">Evrensel işlevler ve kosmik amaç</p>
                    <div className="space-y-2 text-sm">
                      <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-3">
                        <div className="font-medium text-purple-200 mb-2">İç Gezegenler:</div>
                        <div className="text-slate-300">Merkür, Venüs - hızlı değişen amaçlar</div>
                      </div>
                      <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-3">
                        <div className="font-medium text-purple-200 mb-2">Dış Gezegenler:</div>
                        <div className="text-slate-300">Jupiter+ - uzun vadeli kosmik misyon</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Heliocentric Astrology" - Michael Erlewine</div>
                    <div>• "Solar Fire" software documentation</div>
                    <div>• "The Astrology of the Seers" - David Frawley</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Yaklaşımlar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Astrocartography" - Jim Lewis & Arielle Guttman</div>
                    <div>• "The Cosmic Perspective" - Henry Seltzer</div>
                    <div>• "Evolutionary Astrology" - Jeffrey Wolf Green</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Heliocentric Astroloji ileri düzey bir tekniktir ve geleneksel astroloji bilgisi gerektirir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel yorumlama için uzman astrologlarla çalışılmalıdır.
              </p>
            </div>

            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/draconic"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Draconic Harita
              </Link>

              <Link
                href="/about/astrology/advanced-systems/vertex"
                className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
              >
                Vertex Analizi
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}